import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Target, Award, Activity, ArrowLeft, Shield, Lock, Brain, HeadphonesIcon, AlertTriangle, Database, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      // Clone BEFORE reading to prevent "body stream already read"
      const clone = res.clone();
      let data;
      try {
        data = await clone.json();
      } catch {
        try {
          const text = await res.text();
          data = JSON.parse(text);
        } catch {
          throw new Error(`Login fehlgeschlagen (Status: ${res.status})`);
        }
      }
      if (!res.ok) throw new Error(data.detail || 'Login fehlgeschlagen');
      localStorage.setItem('admin_token', data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center px-4">
      <Card className="bg-[#1E1E1E] border-white/10 w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#00BFA6]/10 flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#00BFA6]" />
          </div>
          <CardTitle className="text-2xl text-white">Admin Portal</CardTitle>
          <p className="text-sm text-gray-400 mt-1">Nur autorisierter Zugang</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email</label>
              <input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00BFA6] outline-none"
                placeholder="admin@oddscortex.com"
                required
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Passwort</label>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#00BFA6] outline-none"
                required
              />
            </div>
            {error && (
              <div data-testid="admin-login-error" className="text-red-400 text-sm flex items-center gap-2">
                <Lock className="w-4 h-4" /> {error}
              </div>
            )}
            <Button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold py-3"
            >
              {loading ? 'Authentifiziere...' : 'Admin Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overviewStats, setOverviewStats] = useState(null);
  const [aiPerformance, setAiPerformance] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [recentTips, setRecentTips] = useState([]);
  const [supportData, setSupportData] = useState(null);
  const [aiLearning, setAiLearning] = useState(null);

  const adminFetch = async (path) => {
    const res = await fetch(`${API_URL}/api${path}`, {
      headers: { Authorization: `Bearer ${adminToken}` },
    });
    if (res.status === 401 || res.status === 403) {
      localStorage.removeItem('admin_token');
      setAdminToken('');
      setAuthenticated(false);
      throw new Error('Nicht autorisiert');
    }
    const clone = res.clone();
    let data;
    try {
      data = await clone.json();
    } catch {
      try {
        const text = await res.text();
        data = JSON.parse(text);
      } catch {
        throw new Error('Ungültige Server-Antwort');
      }
    }
    if (!res.ok) throw new Error(data.detail || 'Fehler');
    return data;
  };

  useEffect(() => {
    if (adminToken) {
      verifyAndLoad();
    } else {
      setLoading(false);
    }
  }, [adminToken]);

  const verifyAndLoad = async () => {
    setLoading(true);
    try {
      const [overview, aiPerf, users, tips, support, learning] = await Promise.all([
        adminFetch('/admin/stats/overview'),
        adminFetch('/admin/stats/ai-performance'),
        adminFetch('/admin/stats/users'),
        adminFetch('/admin/stats/recent-tips?limit=10'),
        adminFetch('/admin/support/problems'),
        adminFetch('/admin/ai/learning-progress'),
      ]);
      setOverviewStats(overview);
      setAiPerformance(aiPerf);
      setUserStats(users);
      setRecentTips(tips.tips || []);
      setSupportData(support);
      setAiLearning(learning);
      setAuthenticated(true);
    } catch {
      localStorage.removeItem('admin_token');
      setAdminToken('');
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token) => {
    setAdminToken(token);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken('');
    setAuthenticated(false);
  };

  const COLORS = ['#00BFA6', '#7B1FA2', '#4CAF50', '#FF9800', '#F44336', '#2196F3', '#E91E63', '#009688'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse text-[#00BFA6] text-xl">Admin-Portal wird geladen...</div>
      </div>
    );
  }

  if (!authenticated || !adminToken) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-[#121212]">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00BFA6]" />
              <h1 className="text-2xl font-bold text-[#00BFA6]" data-testid="admin-dashboard-title">Admin Dashboard</h1>
            </div>
          </div>
          <Button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="text-gray-400 hover:text-red-400 text-sm"
          >
            <Lock className="w-4 h-4 mr-2" /> Abmelden
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Nutzer</CardTitle>
              <Users className="w-4 h-4 text-[#00BFA6]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white font-mono" data-testid="stat-total-users">{overviewStats?.total_users || 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Tips</CardTitle>
              <Target className="w-4 h-4 text-[#00BFA6]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white font-mono" data-testid="stat-total-tips">{overviewStats?.total_tips || 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Trefferquote</CardTitle>
              <Award className="w-4 h-4 text-[#4CAF50]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#4CAF50] font-mono" data-testid="stat-win-rate">{overviewStats?.win_rate || 0}%</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Aktive Abos</CardTitle>
              <Activity className="w-4 h-4 text-[#7B1FA2]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white font-mono" data-testid="stat-active-subs">{overviewStats?.active_subscriptions || 0}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="support" className="w-full">
          <TabsList className="bg-[#1E1E1E] border border-white/10 flex flex-wrap">
            <TabsTrigger value="support" data-testid="tab-support">Support-Analyse</TabsTrigger>
            <TabsTrigger value="ai-training" data-testid="tab-ai-training">KI-Training</TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">KI-Performance</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Nutzer</TabsTrigger>
            <TabsTrigger value="recent" data-testid="tab-recent">Aktuelle Tips</TabsTrigger>
          </TabsList>

          {/* Support Problems Tab */}
          <TabsContent value="support" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-400">Gesamt Tickets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white font-mono" data-testid="support-total-tickets">{supportData?.total_tickets || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-400">Heute</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#FF9800] font-mono" data-testid="support-today-tickets">{supportData?.today_tickets || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-400">Bewertung</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-[#00BFA6] font-mono" data-testid="support-avg-rating">
                    {supportData?.avg_rating || 0}/5
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{supportData?.rated_count || 0} Bewertungen</div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <HeadphonesIcon className="w-5 h-5 text-[#00BFA6]" />
                  Kategorisierte Probleme
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(supportData?.problems || []).length === 0 ? (
                  <div className="text-gray-500 text-center py-8">Noch keine kategorisierten Probleme</div>
                ) : (
                  <div className="space-y-3">
                    {(supportData?.problems || []).map((p, i) => (
                      <div key={i} data-testid={`support-problem-${i}`} className="flex items-center justify-between p-4 bg-[#121212] rounded-lg border border-white/5">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className={`w-5 h-5 ${p.count > 10 ? 'text-red-400' : p.count > 5 ? 'text-yellow-400' : 'text-gray-400'}`} />
                          <div>
                            <div className="text-white font-medium">{p.problem_category}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              Zuletzt: {p.last_reported ? new Date(p.last_reported).toLocaleDateString('de-DE') : '-'}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold font-mono text-white">{p.count}x</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Training Tab */}
          <TabsContent value="ai-training" className="space-y-6 mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-400">Matches in DB</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white font-mono" data-testid="ai-data-matches">
                    {(aiLearning?.data_stats?.matches || 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-400">Lern-Eintraege</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#7B1FA2] font-mono">{(aiLearning?.data_stats?.learning_data || 0).toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-400">Fehler-Analysen</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-400 font-mono">{(aiLearning?.data_stats?.error_analyses || 0).toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-400">Datenpunkte</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-[#00BFA6] font-mono" data-testid="ai-data-total">
                    {(aiLearning?.data_stats?.data_points || 0).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Second row: Teams, Players, Standings, Total Records */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardContent className="pt-4 pb-4">
                  <div className="text-xs text-gray-400 mb-1">Teams</div>
                  <div className="text-lg font-bold text-white font-mono">{(aiLearning?.data_stats?.teams || 0).toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardContent className="pt-4 pb-4">
                  <div className="text-xs text-gray-400 mb-1">Spieler</div>
                  <div className="text-lg font-bold text-white font-mono">{(aiLearning?.data_stats?.players || 0).toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardContent className="pt-4 pb-4">
                  <div className="text-xs text-gray-400 mb-1">Tabellen</div>
                  <div className="text-lg font-bold text-white font-mono">{(aiLearning?.data_stats?.standings || 0).toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardContent className="pt-4 pb-4">
                  <div className="text-xs text-gray-400 mb-1">Gesamt Records</div>
                  <div className="text-lg font-bold text-[#00BFA6] font-mono">{(aiLearning?.data_stats?.total || 0).toLocaleString()}</div>
                </CardContent>
              </Card>
            </div>

            {/* Error Type Distribution */}
            {(aiLearning?.error_types || []).length > 0 && (
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    Fehlertyp-Verteilung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={aiLearning?.error_types || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#999" />
                      <YAxis dataKey="_id" type="category" stroke="#999" width={150} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)' }} />
                      <Bar dataKey="count" fill="#F44336" name="Anzahl" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#00BFA6]" />
                  Letzte Training-Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(aiLearning?.sessions || []).length === 0 ? (
                  <div className="text-gray-500 text-center py-8">Noch keine Training-Sessions. Das naechste Training startet um Mitternacht.</div>
                ) : (
                  <div className="space-y-3">
                    {(aiLearning?.sessions || []).map((s, i) => (
                      <div key={i} data-testid={`training-session-${i}`} className="p-4 bg-[#121212] rounded-lg border border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-white font-medium text-sm">{s.session_id}</div>
                          <div className="text-xs text-gray-500">{s.completed_at ? new Date(s.completed_at).toLocaleString('de-DE') : '-'}</div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <div className="text-gray-500 text-xs">Neue Matches</div>
                            <div className="text-white font-mono">{s.new_matches || 0}</div>
                          </div>
                          <div>
                            <div className="text-gray-500 text-xs">Validiert</div>
                            <div className="text-white font-mono">{s.predictions_validated || 0}</div>
                          </div>
                          <div>
                            <div className="text-gray-500 text-xs">Genauigkeit</div>
                            <div className="text-[#00BFA6] font-mono font-bold">{s.session_accuracy || 0}%</div>
                          </div>
                          <div>
                            <div className="text-gray-500 text-xs">Dauer</div>
                            <div className="text-white font-mono">{s.duration_seconds ? `${Math.round(s.duration_seconds)}s` : '-'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {(aiLearning?.errors || []).length > 0 && (
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    Fehler-Analyse (Letzte 20)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(aiLearning?.errors || []).map((e, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg text-sm">
                        <div>
                          <div className="text-white">{e.home_team} vs {e.away_team}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Vorhersage: {e.prediction} | Ergebnis: {e.actual_result}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                          e.error_type === 'high_confidence_miss' ? 'bg-red-500/20 text-red-400' :
                          e.error_type === 'narrow_margin' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-white/10 text-gray-400'
                        }`}>
                          {e.error_type}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {(aiLearning?.accuracy_log || []).length > 0 && (
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Genauigkeit nach Sportart</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={aiLearning?.accuracy_log || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="sport" stroke="#999" tick={{ fontSize: 12 }} />
                      <YAxis stroke="#999" />
                      <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)' }} />
                      <Bar dataKey="accuracy" fill="#00BFA6" name="Genauigkeit %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* AI Performance Tab */}
          <TabsContent value="performance" className="space-y-6 mt-6">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Performance nach Konfidenz</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={aiPerformance?.performance_by_confidence || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="range" stroke="#999" />
                    <YAxis stroke="#999" />
                    <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)' }} />
                    <Bar dataKey="accuracy" fill="#00BFA6" name="Genauigkeit %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Tips nach Sportart</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={overviewStats?.tips_by_sport || []}
                      dataKey="count"
                      nameKey="_id"
                      cx="50%"
                      cy="50%"
                      outerRadius={130}
                      label={(entry) => `${entry._id}: ${entry.count}`}
                    >
                      {(overviewStats?.tips_by_sport || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6 mt-6">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Nutzer nach Abo-Stufe</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={userStats?.users_by_tier || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="_id" stroke="#999" />
                    <YAxis stroke="#999" />
                    <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)' }} />
                    <Bar dataKey="count" fill="#7B1FA2" name="Nutzer" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Abo-Verteilung</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(userStats?.users_by_tier || []).map((tier, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-[#121212] rounded-lg">
                      <span className="text-white font-medium">{tier._id || 'Unbekannt'}</span>
                      <span className="text-[#00BFA6] font-bold font-mono">{tier.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent Tips Tab */}
          <TabsContent value="recent" className="space-y-6 mt-6">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Letzte 10 generierte Tips</CardTitle>
              </CardHeader>
              <CardContent>
                {recentTips.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">Keine Tips vorhanden</div>
                ) : (
                  <div className="space-y-3">
                    {recentTips.map((tip, index) => (
                      <div key={index} className="p-4 bg-[#121212] border border-white/10 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-white font-bold">{tip.team_home} vs {tip.team_away}</div>
                            <div className="text-xs text-gray-400 mt-1">{tip.sport} — {tip.league}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-[#00BFA6] font-mono font-bold">{tip.best_odds}</div>
                            <div className="text-xs text-gray-400">{tip.bookmaker}</div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2 flex-wrap">
                          <span className="text-xs px-2 py-1 rounded bg-[#00BFA6]/20 text-[#00BFA6]">{tip.prediction}</span>
                          <span className="text-xs px-2 py-1 rounded bg-white/10 text-white">{tip.confidence}%</span>
                          {tip.status && (
                            <span className={`text-xs px-2 py-1 rounded ${
                              tip.status === 'won' ? 'bg-green-500/20 text-green-400' :
                              tip.status === 'lost' ? 'bg-red-500/20 text-red-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {tip.status === 'won' ? 'Gewonnen' : tip.status === 'lost' ? 'Verloren' : 'Ausstehend'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
